/**
 * ResultsCount — "N properties", once (obs. 3).
 *
 * THE DEFECT THIS CLOSES. The browse canvas had TWO hand-written result
 * headings: `ListingsSheet`'s desktop pane rendered
 * `<p className="text-sm font-medium">` and `MobileSheet` rendered
 * `<h2 className="font-semibold">`, each calling `t('propertyCount')` itself.
 * Two copies of one sentence is two chances to drift, and they already had:
 * different element, different weight, different size — and the mobile one
 * was left-aligned (`text-align: start`), which is what the owner reported.
 *
 * The fix is not "add `text-center` to the mobile copy". It is that there is
 * one copy: both panes mount THIS, so the two headings cannot disagree about
 * the element, the alignment, the type scale or the string, because there is
 * no second place for them to disagree in.
 *
 * ENTITY layer (`shared/components/listings`), not the listings feature: the
 * desktop pane and the mobile drawer are the two callers today, and a third
 * results surface (a district facet's own header, a saved-search pane) must
 * be able to reach it without importing a feature. It knows `ListingCardData`
 * counts and nothing else — no routes, no filters, no query.
 *
 * `h2`, not `p`: this labels the results REGION, and the drawer's own
 * accessible name is a visually hidden `Drawer.Title`. A heading is what a
 * screen reader's rotor needs in order to jump to the list at all, and the
 * desktop pane had none.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { cn } from '@letsgethome/ui/lib/utils';

export interface ResultsCountProps {
  /**
   * How many results this surface is reporting. The CALLER resolves the
   * fallback (`totalCount ?? listings.length`) — both panes did that
   * identically, and it is a property of the data the pane holds, not of the
   * heading.
   */
  count: number;
  /** Extra layout classes for the pane's own spacing. Never alignment. */
  className?: string;
}

export function ResultsCount({ count, className }: ResultsCountProps) {
  const t = useTranslations('listings.browse');

  return (
    // `text-center` lives HERE and takes no prop: "centred" is the decision
    // this component exists to hold, and a caller that could pass an
    // alignment would be the two hand copies again with extra steps.
    <h2
      data-testid="results-count"
      className={cn('text-center text-sm font-semibold text-foreground', className)}
    >
      {t('propertyCount', { count })}
    </h2>
  );
}
